import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { analytics } from '~entities/amplitude-analytics';

import { combineValidators } from '~shared/lib/combine-validators';
import { plural } from '~shared/lib/plural';
import { InputPhone } from '~shared/react-hook-form-adapters/input-phone';
import { validators } from '~shared/validators';

import { LoadingButton } from '@platform-ui/button';
import { Box, Col } from '@platform-ui/layout';
import Text from '@platform-ui/text';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import {
  useResendCountdown,
  getOtpRequestDelaySeconds,
  getAttemptsLimitMessage,
} from '../../lib';
import {
  isAttemptsLimitReachedAtom,
  lastOtpRequestPhoneAtom,
  lastOtpRequestAtom,
} from '../../model/otp-request-atoms';

export type OtpInitiationError = 'unexpectedError' | 'otpAttemptsLimit';

export type Props = {
  initialPhone?: string;
  onSubmit: (phoneNumber: string) => void;
  isLoading: boolean;
  error?: OtpInitiationError;
};

const getOtpAttemptsLimitMessage = () =>
  'Error verifying the code. Please try again later';

export const OtpInitiation = ({
  initialPhone,
  onSubmit,
  isLoading,
  error,
}: Props) => {
  const { control, handleSubmit, setError, clearErrors } = useForm<{
    phoneNumber: string;
  }>({
    mode: 'all',
    defaultValues: { phoneNumber: initialPhone ?? undefined },
  });

  const [timer, startCountdown, cancelCountdown] = useResendCountdown();
  const isAttemptsLimitReached = useAtomValue(isAttemptsLimitReachedAtom);
  const setLastOtpRequestPhone = useSetAtom(lastOtpRequestPhoneAtom);
  const [lastOtpRequest] = useAtom(lastOtpRequestAtom);

  useEffect(() => {
    if (initialPhone) {
      setLastOtpRequestPhone(initialPhone);
    }
  }, [initialPhone, setLastOtpRequestPhone]);

  useEffect(() => {
    if (error === 'unexpectedError') {
      setError('phoneNumber', {
        message: 'Something went wrong',
      });
    }
    if (error === 'otpAttemptsLimit') {
      setError('phoneNumber', {
        message: getOtpAttemptsLimitMessage(),
      });
    }
  }, [error, setError, initialPhone, startCountdown]);

  useEffect(() => {
    const otpRequestDelay = getOtpRequestDelaySeconds(lastOtpRequest);

    if (otpRequestDelay) {
      startCountdown(otpRequestDelay);
    }
  }, [lastOtpRequest, startCountdown]);

  useEffect(() => {
    if (timer && isAttemptsLimitReached && !isLoading) {
      const otpRequestDelay = getOtpRequestDelaySeconds(lastOtpRequest);
      setError('phoneNumber', {
        message: getAttemptsLimitMessage(otpRequestDelay),
      });
    } else if (!error) {
      clearErrors('phoneNumber');
    }
  }, [
    isAttemptsLimitReached,
    lastOtpRequest,
    error,
    setError,
    clearErrors,
    timer,
    isLoading,
  ]);

  const handleFormSubmit = ({ phoneNumber }: { phoneNumber: string }) => {
    analytics.track({ event: 'auth_phone_number_screen_tap_login' });

    const otpRequestDelay = getOtpRequestDelaySeconds(lastOtpRequest);

    if (otpRequestDelay) {
      startCountdown(otpRequestDelay);
    } else {
      onSubmit(phoneNumber);
    }
  };

  return (
    <Col alignCross="stretch" gaps="s">
      <Text size={15} color="rgba(0, 0, 0, 0.8)">
        Enter your mobile number to log in to Skyro Portal
      </Text>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Controller
          name="phoneNumber"
          control={control}
          rules={{
            required: 'Please, enter your mobile number',
            validate: combineValidators(
              validators.phoneLength(),
              validators.phoneFormat()
            ),
          }}
          render={({ field, ...props }) => (
            <InputPhone
              {...props}
              label="Mobile number"
              field={{
                ...field,
                onChange: (...args) => {
                  if (timer) {
                    cancelCountdown();
                  }
                  field.onChange(...args);
                },
              }}
            />
          )}
        />
        {timer > 0 && !isAttemptsLimitReached && !isLoading && (
          <Box margin="0 0 16px">
            <Text color="rgba(0, 0, 0, 0.4)" size={13}>
              Request a&nbsp;new code in&nbsp;{timer}{' '}
              {plural(timer, 'second', 'seconds')}
            </Text>
          </Box>
        )}
        <LoadingButton
          loading={isLoading}
          disabled={timer > 0}
          theme="primary"
          wide
          size="l"
          type="submit"
        >
          Get a code
        </LoadingButton>
      </form>
    </Col>
  );
};
